import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, User, Briefcase, PieChart, ChevronRight } from "lucide-react";
import { Badge, Loader } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { getPerson, statusVariant, type ArPersonView, type ArPersonCompany } from "../../../lib/ariregister";

function CompanyRow({ c, meta }: { c: ArPersonCompany; meta?: string }) {
  return (
    <Link
      to={`/ariregister/${c.regCode}`}
      className="group flex items-center gap-3 rounded-lg border border-border bg-card/60 px-3 py-2 transition-colors hover:border-primary/40 hover:bg-card"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{c.name}</span>
          {c.status && <Badge variant={statusVariant(c.status)}>{c.statusText || c.status}</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">
          <span className="font-mono">{c.regCode}</span>
          {c.county ? ` · ${c.county}` : ""}
          {meta ? ` · ${meta}` : ""}
        </div>
      </div>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

export function PersonDetail() {
  const t = useT();
  const { hash = "" } = useParams();
  const [view, setView] = useState<ArPersonView | null | "loading">("loading");

  useEffect(() => {
    let alive = true;
    setView("loading");
    getPerson(hash)
      .then((v) => alive && setView(v))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) return alive && setView(null);
        return alive && setView(null);
      });
    return () => {
      alive = false;
    };
  }, [hash]);

  if (view === "loading") {
    return (
      <div className="flex justify-center py-20">
        <Loader size="lg" />
      </div>
    );
  }

  if (!view) {
    return (
      <div className="space-y-4">
        <Back t={t} />
        <div className="rounded-xl border border-dashed border-border bg-card/60 px-6 py-14 text-center text-sm text-muted-foreground shadow-soft">
          {t("ariregister.person.notFound")}
        </div>
      </div>
    );
  }

  // Aktiivsed (lopp_kpv puudub) enne; juriidiline isik → eraldi silt.
  const isLegal = view.personType === "J";

  return (
    <div className="space-y-5">
      <Back t={t} />

      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex size-11 items-center justify-center rounded-full bg-primary/15 text-primary">
          <User className="size-5" />
        </span>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{view.name}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {isLegal ? t("ariregister.person.legal") : t("ariregister.person.physical")} ·{" "}
            {t("ariregister.person.summary", { board: view.board.length, owner: view.shares.length })}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Juhatuses / esindaja ──────────────────────────────────────────── */}
        <section className="rounded-xl border border-border bg-card/80 p-5 shadow-soft backdrop-blur">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Briefcase className="size-4 text-primary" /> {t("ariregister.person.board")}
            <span className="text-muted-foreground">({view.board.length})</span>
          </h2>
          {view.board.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("ariregister.person.none")}</p>
          ) : (
            <div className="space-y-1.5">
              {view.board.map((b) => (
                <CompanyRow key={b.id} c={b.company} meta={b.role} />
              ))}
            </div>
          )}
        </section>

        {/* ── Omanik ────────────────────────────────────────────────────────── */}
        <section className="rounded-xl border border-border bg-card/80 p-5 shadow-soft backdrop-blur">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <PieChart className="size-4 text-primary" /> {t("ariregister.person.owner")}
            <span className="text-muted-foreground">({view.shares.length})</span>
          </h2>
          {view.shares.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("ariregister.person.none")}</p>
          ) : (
            <div className="space-y-1.5">
              {view.shares.map((s) => (
                <CompanyRow key={s.id} c={s.company} meta={s.sharePercent ? `${s.sharePercent}%` : undefined} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Back({ t }: { t: (k: string) => string }) {
  return (
    <Link to="/ariregister/isikud" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" /> {t("ariregister.person.back")}
    </Link>
  );
}
